import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../context/AppContext';
import { HiCheckCircle, HiPlay, HiArrowRight, HiArrowPathRoundedSquare } from 'react-icons/hi2';
import { wizardsData } from '../data/wizardsData';
import WizardModal from '../components/wizards/WizardModal';
import SectionTitle from '../components/SectionTitle';

const Wizards = () => {
  const { t } = useTranslation();
  const { getWizardProgress } = useApp();
  const [selectedWizard, setSelectedWizard] = useState(null);

  const getStatusBadge = (wizardId) => {
    const progress = getWizardProgress(wizardId);
    if (progress.completed) {
      return {
        text: t('wizards.completed'),
        className: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
      };
    }
    if (progress.currentStep > 0) {
      return {
        text: t('wizards.inProgress'),
        className: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300'
      };
    }
    return {
      text: t('wizards.notStarted'),
      className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
    };
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <SectionTitle level={1} icon={HiArrowPathRoundedSquare}>
          {t('wizards.title')}
        </SectionTitle>
        <p className="text-gray-600 dark:text-gray-400">
          Procédures guidées étape par étape pour vos démarches importantes
        </p>
      </div>

      {/* Wizards grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {Object.values(wizardsData).map((wizard) => {
          const progress = getWizardProgress(wizard.id);
          const status = getStatusBadge(wizard.id);

          return (
            <div
              key={wizard.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm hover:shadow-md transition-all cursor-pointer group"
              onClick={() => setSelectedWizard(wizard.id)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                    {t(`wizards.${wizard.id}.title`)}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t(`wizards.${wizard.id}.description`)}
                  </p>
                </div>
                {progress.completed && (
                  <HiCheckCircle className="h-6 w-6 text-green-500 dark:text-green-400 flex-shrink-0 ml-2" />
                )}
              </div>

              {/* Progress bar */}
              {!progress.completed && progress.currentStep > 0 && (
                <div className="mb-4">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-teal-600 dark:bg-teal-500 h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(progress.currentStep / wizard.steps) * 100}%`
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('wizards.stepOf', {
                      current: progress.currentStep,
                      total: wizard.steps
                    })}
                  </p>
                </div>
              )}

              {/* Status and action */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.className}`}>
                  {status.text}
                </span>
                <button className="flex items-center space-x-1 text-sm text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium">
                  {progress.currentStep > 0 && !progress.completed ? (
                    <>
                      <span>{t('wizards.continue')}</span>
                      <HiArrowRight className="h-4 w-4" />
                    </>
                  ) : progress.completed ? (
                    <>
                      <span>Revoir</span>
                      <HiPlay className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      <span>{t('wizards.start')}</span>
                      <HiPlay className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Wizard Modal */}
      {selectedWizard && (
        <WizardModal
          wizardId={selectedWizard}
          onClose={() => setSelectedWizard(null)}
        />
      )}
    </div>
  );
};

export default Wizards;
